/**
 * Branch-scope hooks shared by every branch control (and by the pages that need
 * to reason about scope without rendering one).
 *
 * Split out of `components/branch-picker.tsx` so that file exports components
 * only — a module mixing components with plain exports breaks Fast Refresh for
 * everything in it.
 */
import { useQuery } from '@tanstack/react-query';
import { listBranches, type Branch } from './api';
import { useAuthStore, useIsSuperAdmin } from './auth-store';

/**
 * The branch list, shared by every picker on the page — one network call no
 * matter how many render.
 *
 * NOTE: `GET /branches` is unauthenticated today (no preHandler on the route).
 * Out of scope here; flagged so nobody assumes the picker is what protects it.
 */
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    staleTime: 5 * 60_000,
  });
}

/**
 * The branches this admin is confined to; empty = unconfined. Mirrors the
 * server's `effectiveBranchScopeIds`, including its fallback to the legacy
 * single POS pin, so the UI and the clamp can't disagree.
 */
export function useConfinedBranchIds(): string[] {
  const ids = useAuthStore((s) => s.user?.branchIds);
  const pin = useAuthStore((s) => s.user?.branchId ?? null);
  const isSuper = useIsSuperAdmin();
  if (isSuper) return [];
  if (ids && ids.length > 0) return ids;
  return pin ? [pin] : [];
}

/**
 * True when this admin is confined at all. The server clamps their results
 * regardless, so an unrestricted picker would only be a lie — see
 * `scopedFallback` for what renders instead when they hold exactly one branch.
 */
export function useIsBranchScoped(): boolean {
  return useConfinedBranchIds().length > 0;
}

const label = (b: Branch) => `${b.name} (${b.code})`;

/** The branch a scoped operator is pinned to, resolved to a display label. */
export function useScopedBranchLabel(): string | null {
  const confined = useConfinedBranchIds();
  const { data: branches } = useBranches();
  if (confined.length !== 1) return null;
  const b = (branches ?? []).find((x) => x.id === confined[0]);
  return b ? label(b) : null;
}

/**
 * The branch selection a report is run for. THREE distinct states — this is a
 * tri-state, not a list, and collapsing it back to a plain array reintroduces
 * the ambiguity it exists to remove:
 *
 *   `[]`      nothing ticked. The report does NOT run. This is the default.
 *   `null`    "All" ticked — send NO `branchId` param, i.e. tenant-wide.
 *   `[a, b]`  exactly those branches.
 *
 * `null` and a fully-ticked list are NOT the same query. `orderBranchFilter`
 * (apps/api/src/services/reports-extended.ts) emits
 * `COALESCE(ship_from_branch_id, branch_id) IN (…)`, and `IN` excludes NULLs —
 * so an order with no branch appears under "All" and vanishes the moment every
 * box is ticked individually. That asymmetry is asserted in
 * apps/api/src/routes/reports-sales.test.ts. Hence `toggle` below must never
 * auto-collapse a full selection to `null`.
 */
export type BranchSelection = string[] | null;

/**
 * The branches this operator can actually tick.
 *
 * Exported because the Reports page needs to tell three situations apart, and
 * only two of them are visible from the component:
 *
 *   `isPending`  the branch list hasn't loaded. Decide NOTHING yet — an empty
 *                `options` here is just an unresolved query, and treating it as
 *                "no choice offered" would run the report tenant-wide on first
 *                paint and then blank it when the branches land.
 *   `hidden`     genuinely no choice to offer (confined to exactly one branch,
 *                or nothing matches the type filter). The page must treat this
 *                as "All", or an operator who never sees the control would have
 *                a report that can never run.
 *   otherwise    real options; the operator picks.
 */
export function useBranchOptions(filterType?: string): {
  options: Branch[];
  confined: string[];
  isPending: boolean;
  hidden: boolean;
} {
  const { data: branches, isPending } = useBranches();
  const confined = useConfinedBranchIds();
  const options = (branches ?? [])
    .filter((b) => !filterType || b.type === filterType)
    .filter((b) => confined.length === 0 || confined.includes(b.id));
  // Exactly one branch: nothing to tick. Matches the Reports filter row's
  // long-standing `scopedFallback='hidden'` treatment for the single picker.
  const hidden = !isPending && (confined.length === 1 || options.length === 0);
  return { options, confined, isPending, hidden };
}
